/**
 * Socket Client
 * Production-ready, modular WebSocket client with auto-reconnect,
 * lifecycle handling, and heartbeat mechanism
 * Configure via constructor options - no project-specific dependencies.
 */

import {
  CONNECTION_STATES,
  WS_READY_STATES,
  DEFAULT_CONFIG,
  SYSTEM_EVENTS,
  constructWebSocketUrl,
  addTokenToUrl,
  generateRequestId,
  calculateReconnectDelay,
  safeJsonParse,
  isTabVisible,
  formatTime,
  clearTimer,
  clearIntervalSafe,
  safeCallback,
} from './utils';

const TAB_VISIBLE_RECONNECT_STATES = new Set([
  CONNECTION_STATES.DISCONNECTED,
  CONNECTION_STATES.ERROR,
  CONNECTION_STATES.FAILED,
  CONNECTION_STATES.RECONNECTING,
]);

/**
 * SocketClient Class
 * Manages WebSocket connections with auto-reconnect, heartbeat, and lifecycle handling
 */
class SocketClient {
  constructor(config = {}) {
    // Connection state
    this.socket = null;
    this.url = null;
    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.isManuallyClosed = false;
    
    // Reconnection settings
    this.reconnectInterval = config.reconnectInterval ?? DEFAULT_CONFIG.RECONNECT_INTERVAL;
    this.maxReconnectAttempts = config.maxReconnectAttempts ?? DEFAULT_CONFIG.MAX_RECONNECT_ATTEMPTS;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    
      // enableInactivityDisconnect: boolean (default: false) - Enable/disable auto-disconnect on tab hide
      // inactivityTimeout: number (default: 5 minutes) - Timeout in milliseconds before disconnect
      this.inactivityDisconnectTimer = null;
      this.enableInactivityDisconnect = config.enableInactivityDisconnect === true; // Default: false
    this.inactivityTimeout = config.inactivityTimeout ?? DEFAULT_CONFIG.INACTIVITY_TIMEOUT;
    this.inactivityTimeoutFormatted = formatTime(this.inactivityTimeout);
    
    // Heartbeat settings
    this.heartbeatTimerId = null;
    this.heartbeatIntervalMs =
      config.heartbeatInterval ?? DEFAULT_CONFIG.HEARTBEAT_INTERVAL;
    // Pong watchdog: detects half-open sockets
    this.pongTimeout = config.pongTimeout ?? DEFAULT_CONFIG.PONG_TIMEOUT;
    this.pongWatchdog = null;

    // Event listeners
    this.listeners = new Map();
    
    // Pending requests (request/response pattern)
    this.pendingRequests = new Map();
    this.requestIdCounter = 0;
    
    // Configuration - must be provided via config
    // If webSocketUrl is not provided, construct it from current host URL
    this.webSocketUrl = config.webSocketUrl || (typeof window !== 'undefined' && constructWebSocketUrl(window.location.href)) || null;
    this.authToken = config.authToken || '';
    // Optional token provider: re-read token on every (re)connect for rotated/refreshed tokens
    this.authTokenProvider = typeof config.authTokenProvider === 'function' ? config.authTokenProvider : null;

    // Lifecycle handlers
    this.visibilityHandler = null;
    this.beforeUnloadHandler = null;
    this.pageHideHandler = null;
    this.onlineHandler = null;
    
    // Setup lifecycle listeners
    this._setupLifecycleListeners();
  }

  /**
   * Sets up lifecycle event listeners for tab visibility and page unload
   */
  _setupLifecycleListeners() {
    this.visibilityHandler = () => {
      if (isTabVisible()) {
        this._handleTabVisible();
      } else {
        this._handleTabHidden();
      }
    };

    this.beforeUnloadHandler = () => {
      this.cleanup();
    };

    this.pageHideHandler = () => {
      this.cleanup();
    };

    this.onlineHandler = () => this._handleOnline();

    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('online', this.onlineHandler);
  }

  _removeEventListener(target, event, handler) {
    if (handler && target) {
      target.removeEventListener(event, handler);
    }
    return null;
  }

  _removeLifecycleListeners() {
    this.visibilityHandler = this._removeEventListener(
      document,
      'visibilitychange',
      this.visibilityHandler
    );
    this.beforeUnloadHandler = this._removeEventListener(
      window,
      'beforeunload',
      this.beforeUnloadHandler
    );
    this.pageHideHandler = this._removeEventListener(
      window,
      'pagehide',
      this.pageHideHandler
    );
    this.onlineHandler = this._removeEventListener(
      window,
      'online',
      this.onlineHandler
    );
  }

  async connect(customUrl = null) {
    this._clearInactivityTimer();
    
    if (this.socket?.readyState === WS_READY_STATES.OPEN) {
      return;
    }

    if (this.socket?.readyState === WS_READY_STATES.CONNECTING) {
      await this._waitForConnection();
      return;
    }

    try {
      await this._attemptConnection(customUrl);
    } catch (error) {
      console.error('[SocketClient] Connection attempt failed:', error);
      throw error;
    }
  }

  _waitForConnection() {
    return new Promise((resolve, reject) => {
      const checkConnection = setInterval(() => {
        if (this.socket?.readyState === WS_READY_STATES.OPEN) {
          clearInterval(checkConnection);
          resolve();
        } else if (this.socket?.readyState === WS_READY_STATES.CLOSED) {
          clearInterval(checkConnection);
          this._attemptConnection().then(resolve).catch(reject);
        }
      }, DEFAULT_CONFIG.CONNECTION_CHECK_INTERVAL);
    });
  }

  _attemptConnection(customUrl = null) {
    return new Promise((resolve, reject) => {
      this.url = customUrl || this.webSocketUrl;
      if (!this.url) {
        const error = new Error('WebSocket URL not configured');
        this._notifyConnectionStateChange(CONNECTION_STATES.ERROR);
        reject(error);
        return;
      }

      // Resolve token fresh on every attempt so rotated tokens are picked up
      const token = this.authTokenProvider ? (this.authTokenProvider() || '') : this.authToken;
      const wsUrl = addTokenToUrl(this.url, token);
      this.isManuallyClosed = false;
      this._notifyConnectionStateChange(CONNECTION_STATES.CONNECTING);

      try {
        this.socket = new WebSocket(wsUrl);
        this._setupEventHandlers(resolve, reject);
      } catch (error) {
        this._notifyConnectionStateChange(CONNECTION_STATES.ERROR);
        reject(error);
      }
    });
  }

  _setupEventHandlers(resolve, reject) {
    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this._startHeartbeat();
      this._notifyConnectionStateChange(CONNECTION_STATES.CONNECTED);
      resolve();
    };

    this.socket.onmessage = (event) => {
      const message = safeJsonParse(event.data);
      if (message) {
        this._handleMessage(message);
      }
    };

    this.socket.onerror = () => {
      this._notifyConnectionStateChange(CONNECTION_STATES.ERROR);
      if (this.connectionState === CONNECTION_STATES.CONNECTING) {
        reject(new Error('WebSocket connection error'));
      }
    };

    this.socket.onclose = () => {
      this._stopHeartbeat();
      this.socket = null;

      // Fail-fast in-flight requests so callers don't hang until REQUEST_TIMEOUT
      this._rejectPendingRequests('Connection closed, will reconnect');

      if (!this.isManuallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
        this._scheduleReconnect();
      } else {
        const finalState =
          this.reconnectAttempts >= this.maxReconnectAttempts
            ? CONNECTION_STATES.FAILED
            : CONNECTION_STATES.DISCONNECTED;
        this._notifyConnectionStateChange(finalState);
      }
    };
  }

  _scheduleReconnect() {
    this._clearReconnectTimer();
    this.reconnectAttempts++;
    this._notifyConnectionStateChange(CONNECTION_STATES.RECONNECTING);

    const delay = calculateReconnectDelay(
      this.reconnectAttempts,
      this.reconnectInterval,
      DEFAULT_CONFIG.MAX_RECONNECT_DELAY
    );

    this.reconnectTimer = setTimeout(async () => {
      if (this.isManuallyClosed) return;
      // Skip if offline; 'online' handler will resume reconnection
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        console.log('[SocketClient] Offline, waiting for network');
        return;
      }
      try {
        await this.connect(this.url);
      } catch (error) {
        console.error('[SocketClient] Reconnection attempt failed:', error);
      }
    }, delay);
  }

  _handleMessage(message) {
    const event = message.event || message.eventType || (message.requestId ? message.requestId : null);
    const data = message.data || message.eventMessage || message;
    const { requestId, error } = message;

    // Any inbound frame proves the socket is alive
    this._clearPongWatchdog();

    if (requestId && this.pendingRequests.has(requestId)) {
      const { resolve, reject, timeout } = this.pendingRequests.get(requestId);
      clearTimeout(timeout);
      this.pendingRequests.delete(requestId);
      error ? reject(new Error(error)) : resolve(data);
      return;
    }

    // Server may respond with { type: "pong", data: {...} } instead of event: "PONG"
    const messageType = message.type;
    const isPong =
      event === SYSTEM_EVENTS.PONG ||
      (typeof messageType === 'string' && messageType.toLowerCase() === 'pong');
    if (isPong) return;

    if (event) {
      this._notifyListeners(event, data);
    }
  }

  _checkConnection() {
    if (!this.socket || this.socket.readyState !== WS_READY_STATES.OPEN) {
      throw new Error('WebSocket not connected');
    }
  }

  _sendMessage(message) {
    this._checkConnection();
    this.socket.send(JSON.stringify({ ...message, eventTime: Date.now() }));
  }

  async emit(event, data = {}) {
    try {
      this._sendMessage({ eventType: event, data});
    } catch (error) {
      console.error('[SocketClient] Emit error:', error);
      throw error;
    }
  }

  request(event, data = {}, timeout = DEFAULT_CONFIG.REQUEST_TIMEOUT) {
    return new Promise((resolve, reject) => {
      try {
        this._checkConnection();
        const requestId = generateRequestId(++this.requestIdCounter);

        const timeoutId = setTimeout(() => {
          this.pendingRequests.delete(requestId);
          reject(new Error(`Request timeout: ${event}`));
        }, timeout);

        this.pendingRequests.set(requestId, { resolve, reject, timeout: timeoutId });
        this._sendMessage({ event, data, requestId });
      } catch (error) {
        reject(error);
      }
    });
  }

  subscribe(event, callback) {
    if (typeof callback !== 'function') {
      console.warn('[SocketClient] Invalid callback provided for subscribe');
      return () => {};
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event).add(callback);
    return () => this.unsubscribe(event, callback);
  }

  unsubscribe(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
      if (this.listeners.get(event).size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  _notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        safeCallback(callback, [data], `[SocketClient] Listener for ${event}`);
      });
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimerId = setInterval(() => {
      if (this.socket?.readyState === WS_READY_STATES.OPEN) {
        try {
          this._sendMessage({ event: SYSTEM_EVENTS.PING });
          // Arm watchdog; if no frame arrives within pongTimeout, force-close to trigger reconnect
          this._startPongWatchdog();
        } catch (error) {
          console.error('[SocketClient] Heartbeat error:', error);
        }
      }
    }, this.heartbeatIntervalMs);
  }

  _startPongWatchdog() {
    this._clearPongWatchdog();
    this.pongWatchdog = setTimeout(() => {
      console.warn('[SocketClient] PONG timeout, forcing reconnect');
      if (this.socket) {
        try {
          // 4000 = app-defined close code for "pong timeout"
          this.socket.close(4000, 'pong timeout');
        } catch (error) {
          console.error('[SocketClient] Error force-closing dead socket:', error);
        }
      }
    }, this.pongTimeout);
  }

  _clearPongWatchdog() {
    this.pongWatchdog = clearTimer(this.pongWatchdog);
  }

  _stopHeartbeat() {
    this.heartbeatTimerId = clearIntervalSafe(this.heartbeatTimerId);
    this._clearPongWatchdog();
  }

  _flushReconnectBackoff() {
    this._clearReconnectTimer();
    this.reconnectAttempts = 0;
  }

  _tryReconnectNow(logMessage, failedMessage) {
    this._flushReconnectBackoff();
    console.log(logMessage);
    this.connect().catch((error) => {
      console.error(failedMessage, error);
    });
  }

  _handleOnline() {
    if (this.isManuallyClosed) return;
    if (this.connectionState === CONNECTION_STATES.CONNECTED) return;
    this._tryReconnectNow(
      '[SocketClient] Network online, reconnecting immediately',
      '[SocketClient] Failed to reconnect on network online:'
    );
  }

  _notifyConnectionStateChange(state) {
    this.connectionState = state;
    this._notifyListeners(SYSTEM_EVENTS.CONNECTION_STATE_CHANGE, { state });
  }

  _handleTabVisible() {
    this._clearInactivityTimer();

    if (this.isManuallyClosed) {
      return;
    }

    if (!TAB_VISIBLE_RECONNECT_STATES.has(this.connectionState)) {
      return;
    }

    this._tryReconnectNow(
      '[SocketClient] Tab became visible, reconnecting...',
      '[SocketClient] Failed to reconnect on tab visible:'
    );
  }

  _handleTabHidden() {
    if (!this.enableInactivityDisconnect) {
      console.log('[SocketClient] Tab became hidden, keeping connection alive (inactivity disconnect disabled)');
      return;
    }

    if (this.connectionState === CONNECTION_STATES.CONNECTED) {
      console.log(
        `[SocketClient] Tab became hidden, will disconnect after ${this.inactivityTimeoutFormatted} of inactivity`
      );
      this._scheduleInactivityDisconnect();
    }
  }

  _scheduleInactivityDisconnect() {
    if (!this.enableInactivityDisconnect) {
      return;
    }

    this._clearInactivityTimer();
    
    this.inactivityDisconnectTimer = setTimeout(() => {
      if (this.connectionState === CONNECTION_STATES.CONNECTED && !isTabVisible()) {
        console.log(
          `[SocketClient] Tab inactive for ${this.inactivityTimeoutFormatted}, disconnecting...`
        );
        this._disconnectForInactivity();
      }
      this.inactivityDisconnectTimer = null;
    }, this.inactivityTimeout);
  }

  _clearInactivityTimer() {
    this.inactivityDisconnectTimer = clearTimer(this.inactivityDisconnectTimer);
  }

  _closeSocket(setManuallyClosed = false) {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    if (setManuallyClosed) {
      this.isManuallyClosed = true;
    }
  }

  _clearAllTimers() {
    this._clearInactivityTimer();
    this._stopHeartbeat();
    this._clearReconnectTimer();
  }

  _disconnectForInactivity() {
    this._clearAllTimers();
    this._closeSocket(false);
    this._notifyConnectionStateChange(CONNECTION_STATES.DISCONNECTED);
  }

  _clearReconnectTimer() {
    this.reconnectTimer = clearTimer(this.reconnectTimer);
  }

  _rejectPendingRequests(errorMessage = 'Connection closed') {
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimer(timeout);
      reject(new Error(errorMessage));
    });
    this.pendingRequests.clear();
  }

  disconnect() {
    this._clearAllTimers();
    this._rejectPendingRequests();
    this._closeSocket(true);
    this._notifyConnectionStateChange(CONNECTION_STATES.DISCONNECTED);
  }

  cleanup() {
    this.disconnect();
    this._removeLifecycleListeners();
    this.listeners.clear();
  }

  getState() {
    return {
      state: this.connectionState,
      readyState: this.socket?.readyState,
      isConnected: this.socket?.readyState === WS_READY_STATES.OPEN,
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  isConnected() {
    return this.socket?.readyState === WS_READY_STATES.OPEN;
  }
}

// Export class for custom instances
export default SocketClient;

